import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CurrentOrPreviousGroup, Member } from '../../../../../../api/groups';
import { FilterSearchParam } from '../../../../../../shared-components/elementis/types/FilterSearchParam';
import { useResultsDataContext } from '../../ResultsDataContext';
import groupBy from 'lodash/groupBy';
import mapValues from 'lodash/mapValues';
import TABS, { Tab } from '../../../../../../shared-components/elementis/types/ResultsPageTabs';

type GroupAndSelectedMemberId = [string, string];

export function useSelectedMembers({
  selectedGroups,
  selectedTab,
}: {
  selectedGroups: CurrentOrPreviousGroup[];
  selectedTab: Tab;
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { members: membersPerGroup } = useResultsDataContext();

  const concatenatedMemberIds = searchParams.get(FilterSearchParam.Members);

  const selectedMembersPerGroup = useMemo(() => {
    if (!concatenatedMemberIds) return membersPerGroup;

    const groupIdAndMemberId = concatenatedMemberIds
      .split(',')
      .map(concatenatedId => concatenatedId.split(';')) as GroupAndSelectedMemberId[];

    const selectedMemberIdsPerGroup = groupBy(groupIdAndMemberId, ([groupId]) => groupId);

    return mapValues(selectedMemberIdsPerGroup, groupAndMemberIds => {
      return groupAndMemberIds
        .map(([groupId, memberId]) => membersPerGroup[groupId]?.find(member => member.id === memberId))
        .filter(Boolean);
    }) as Record<string, Member[]>;
  }, [concatenatedMemberIds, membersPerGroup]);

  const totalMembersOfGroupsCount = useMemo(() => {
    if (selectedTab === TABS.STUDENT) {
      return membersPerGroup[selectedGroups[0]?.id]?.length || 0;
    }

    return Object.values(membersPerGroup).flat().length;
  }, [membersPerGroup, selectedGroups, selectedTab]);

  const setSelectedMembers = useCallback(
    (members: string[]) => {
      setSearchParams(prev => {
        const newParams = new URLSearchParams(prev);

        if (members.length === 0 || totalMembersOfGroupsCount === 0 || members.length === totalMembersOfGroupsCount) {
          newParams.delete(FilterSearchParam.Members);
        } else if (totalMembersOfGroupsCount > 0 && members.length !== totalMembersOfGroupsCount) {
          newParams.set(FilterSearchParam.Members, members.join(','));
        }

        return newParams;
      });
    },
    [setSearchParams, totalMembersOfGroupsCount],
  );

  return [selectedMembersPerGroup, setSelectedMembers] as const;
}
